import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { range } from '@/lib/random'
import { useSequence } from '@/hooks/use-sequence'
import { SceneBadge } from '@/components/landing/games/scene-ui'

type GemKind = 'ruby' | 'citrine' | 'emerald' | 'aqua' | 'sapphire' | 'amethyst'

type GemShape = {
    color: string
    outline: string
    facets: string
    shine: string
}

const GEMS: Record<GemKind, GemShape> = {
    ruby: {
        color: '#FF4D6D',
        outline: 'M13 7 H27 L34 15 L20 34 L6 15 Z',
        facets: 'M6 15 H34 M13 7 L17 15 L20 7 L23 15 L27 7 M17 15 L20 34 L23 15',
        shine: 'M13 7 H20 L17 15 H6 Z'
    },
    citrine: {
        color: '#FFC94D',
        outline: 'M20 4 L34 12 V28 L20 36 L6 28 V12 Z',
        facets: 'M20 11 L28 15.5 V24.5 L20 29 L12 24.5 V15.5 Z M20 4 V11 M34 12 L28 15.5 M34 28 L28 24.5 M20 36 V29 M6 28 L12 24.5 M6 12 L12 15.5',
        shine: 'M20 4 L6 12 L12 15.5 L20 11 Z'
    },
    emerald: {
        color: '#3DDC97',
        outline: 'M13 5 H27 L33 11 V29 L27 35 H13 L7 29 V11 Z',
        facets: 'M14 11 H26 V29 H14 Z M13 5 L14 11 M27 5 L26 11 M33 29 L26 29 M27 35 L26 29 M13 35 L14 29 M7 11 L14 11',
        shine: 'M13 5 H27 L26 11 H14 Z'
    },
    aqua: {
        color: '#4DD9FF',
        outline: 'M20 5 L35 32 H5 Z',
        facets: 'M20 14 L28 28 H12 Z M20 5 V14 M35 32 L28 28 M5 32 L12 28',
        shine: 'M20 5 L20 14 L12 28 L5 32 Z'
    },
    sapphire: {
        color: '#5B7CFF',
        outline: 'M20 5 A15 15 0 1 1 19.99 5 Z',
        facets: 'M20 11 L26.4 13.6 L29 20 L26.4 26.4 L20 29 L13.6 26.4 L11 20 L13.6 13.6 Z M20 5 V11 M30.6 9.4 L26.4 13.6 M35 20 H29 M30.6 30.6 L26.4 26.4 M20 35 V29 M9.4 30.6 L13.6 26.4 M5 20 H11 M9.4 9.4 L13.6 13.6',
        shine: 'M20 5 A15 15 0 0 0 5 20 H11 L13.6 13.6 L20 11 Z'
    },
    amethyst: {
        color: '#B57CFF',
        outline: 'M20 4 C30 12 30 28 20 36 C10 28 10 12 20 4 Z',
        facets: 'M20 4 V36 M14.5 20 H25.5 M20 4 L14.5 20 L20 36 L25.5 20 Z',
        shine: 'M20 4 C14 9 12.5 15 13 20 H14.5 Z'
    }
}

const KINDS = Object.keys(GEMS) as GemKind[]

const PATTERNS: Record<string, string> = {
    '5': 'Five of a kind',
    '41': 'Four of a kind',
    '32': 'Full house',
    '311': 'Three of a kind',
    '221': 'Two pair',
    '2111': 'One pair',
    '11111': 'No match'
}

const evaluate = (gems: GemKind[]): { name: string, matched: Set<GemKind> } => {
    const counts = new Map<GemKind, number>()
    gems.forEach(gem => counts.set(gem, (counts.get(gem) ?? 0) + 1))

    const key = [...counts.values()].sort((a, b) => b - a).join('')
    const matched = new Set([...counts.entries()].filter(([, count]) => count >= 2).map(([gem]) => gem))

    return { name: PATTERNS[key] ?? 'No match', matched }
}

interface GemGlyphProps {
    kind: GemKind
    className?: string
}

const GemGlyph: FC<GemGlyphProps> = ({ kind, className }) => {
    const gem = GEMS[kind]

    return (
        <svg viewBox='0 0 40 40' aria-hidden='true' className={className}>
            <path d={gem.outline} fill={gem.color} />
            <path d={gem.shine} fill='rgba(255, 255, 255, 0.32)' />
            <path d={gem.facets} fill='none' stroke='rgba(255, 255, 255, 0.5)' strokeWidth='0.9' strokeLinejoin='round' />
            <path d={gem.outline} fill='none' stroke='rgba(0, 0, 0, 0.3)' strokeWidth='1' strokeLinejoin='round' />
        </svg>
    )
}

const POSTER: GemKind[] = ['sapphire', 'ruby', 'sapphire', 'citrine', 'ruby']

const DiamondsScene: FC<SceneProps> = ({ active }) => {
    const [gems, setGems] = useState<GemKind[]>(POSTER)
    const [evaluated, setEvaluated] = useState(true)

    useSequence(active, async ({ wait }) => {
        setGems([])
        setEvaluated(false)

        if (!(await wait(500))) return

        const draw = range(5).map(() => KINDS[Math.floor(Math.random() * KINDS.length)])

        for (let i = 0; i < draw.length; i++) {
            setGems(draw.slice(0, i + 1))
            if (!(await wait(240))) return
        }

        if (!(await wait(500))) return

        setEvaluated(true)
        await wait(2100)
    })

    const { name, matched } = evaluate(gems)

    return (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-5 px-4 pb-6 pt-14 @sm:px-6'>
            <div className='flex items-center gap-2 rounded-[22px] border border-white/[0.06] bg-[#111115] p-2 shadow-[inset_0_2px_12px_rgba(0,0,0,0.65)] @sm:gap-2.5 @sm:p-2.5'>
                {range(5).map(slot => {
                    const kind = gems[slot]
                    const lit = evaluated && kind !== undefined && matched.has(kind)

                    return (
                        <div
                            key={slot}
                            className={cn(
                                'relative grid size-11 place-items-center rounded-2xl bg-[#18181d] shadow-[inset_0_3px_8px_rgba(0,0,0,0.65)] transition-all duration-300 @sm:size-[64px]',
                                lit && 'bg-ember/10 ring-1 ring-inset ring-ember/50'
                            )}
                        >
                            {kind && (
                                <div className='animate-gem-drop'>
                                    <GemGlyph
                                        kind={kind}
                                        className={cn(
                                            'size-8 transition-all duration-300 @sm:size-11',
                                            evaluated && !lit && 'opacity-35 saturate-50',
                                            lit && '-translate-y-0.5 drop-shadow-[0_0_12px_rgba(255,255,255,0.35)]'
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <div className='flex items-center gap-3'>
                <SceneBadge tone={evaluated ? (matched.size > 0 ? 'ember' : 'muted') : 'default'}>
                    {evaluated ? name : 'Gems landing'}
                </SceneBadge>
                <span className='font-mono text-[10px] uppercase tracking-[0.12em] text-steel'>
                    Medium risk
                </span>
            </div>
        </div>
    )
}

export default DiamondsScene
